import { TaskStatus, TaskDisplayValues } from '@models/task.model';
import { BudgetRequestStatus } from '@models/budget.model';
import COLORS from '@utils/color';
import { parseDate } from '@utils/formatDate';
import type en from '@locales/en';

export interface TaskDisplayInfo {
  displayValue: TaskDisplayValues;
  color: string;
  /**
   * Sprint 28 change request (global sweep, defect fix). This used to carry hardcoded English
   * text ('Complete', 'Pending Client Approval', 'Upcoming', ...) straight into JSX at four live
   * call sites (`TaskDetailHeader`, `AssignedTasks`, `GeneralTab`, `TaskRow` via `TaskTable`) —
   * a Spanish user read English task-status labels. `label` now just mirrors `displayValue` (the
   * enum itself, e.g. `"UPCOMING"`). Every caller must translate through
   * `translateTaskDisplayValue` below before rendering — do not render `label` directly. Mirrors
   * mobile's identical fix in `redesign/src/utils/taskDisplay.utils.ts`.
   */
  label: string;
}

/**
 * Determines the display value, color, and label for a task based on its status and other properties
 */
export function getTaskStatusDisplay(
  task: any,
  budgetRequests: any[] = [],
): TaskDisplayInfo {
  // Handle null/undefined task
  if (!task) {
    return {
      displayValue: TaskDisplayValues.UNASSIGNED,
      color: COLORS.orange,
      label: TaskDisplayValues.UNASSIGNED,
    };
  }

  // Direct status mapping first
  if (task.status === TaskStatus.COMPLETE) {
    return {
      displayValue: TaskDisplayValues.COMPLETE,
      color: COLORS.lightBlue,
      label: TaskDisplayValues.COMPLETE,
    };
  }

  if (task.status === TaskStatus.CLIENT_APPROVAL) {
    return {
      displayValue: TaskDisplayValues.CLIENT_APPROVAL,
      color: COLORS.darkGreen,
      label: TaskDisplayValues.CLIENT_APPROVAL,
    };
  }

  if (task.status === TaskStatus.INTERNAL_APPROVAL) {
    return {
      displayValue: TaskDisplayValues.INTERNAL_APPROVAL,
      color: COLORS.darkerGreen,
      label: TaskDisplayValues.INTERNAL_APPROVAL,
    };
  }

  if (task.status === TaskStatus.INACTIVE) {
    return {
      displayValue: TaskDisplayValues.INACTIVE,
      color: COLORS.orange,
      label: TaskDisplayValues.INACTIVE,
    };
  }

  if (task.status === TaskStatus.REJECTED) {
    return {
      displayValue: TaskDisplayValues.REJECTED,
      color: COLORS.error,
      label: TaskDisplayValues.REJECTED,
    };
  }

  if (task.status === TaskStatus.CANCELED) {
    return {
      displayValue: TaskDisplayValues.CANCELED,
      color: COLORS.cancel,
      label: TaskDisplayValues.CANCELED,
    };
  }

  if (task.status === TaskStatus.DELEGATION_CANCELED) {
    return {
      displayValue: TaskDisplayValues.DELEGATION_CANCELED,
      color: COLORS.violet,
      label: TaskDisplayValues.DELEGATION_CANCELED,
    };
  }

  // For ACTIVE tasks, determine the specific display value
  if (task.status === TaskStatus.ACTIVE) {
    // Check for budget requests first
    const safeBudgetRequests: any[] = Array.isArray(budgetRequests)
      ? budgetRequests
      : [];
    
    // Check for active budget requests first (highest priority for active tasks)
    const hasActiveBudgetRequests = safeBudgetRequests.some(
      (budgetRequest: any) =>
        budgetRequest.status === BudgetRequestStatus.ACTIVE,
    );

    if (hasActiveBudgetRequests) {
      return {
        displayValue: TaskDisplayValues.IN_NEGOTIATIONS,
        color: COLORS.negociations,
        label: TaskDisplayValues.IN_NEGOTIATIONS,
      };
    }

    // Check for any pending budget requests (not yet active)
    const hasPendingBudgetRequests = safeBudgetRequests.some(
      (budgetRequest: any) =>
        budgetRequest.status === BudgetRequestStatus.PENDING,
    );

    if (hasPendingBudgetRequests) {
      return {
        displayValue: TaskDisplayValues.IN_NEGOTIATIONS,
        color: COLORS.negociations,
        label: TaskDisplayValues.IN_NEGOTIATIONS,
      };
    }

    const now = new Date();

    // Task.startTime/endTime are DateOnly (see redesign/CLAUDE.md's "Date Handling"): parsed
    // through parseDate() at local midnight, never the plain Date constructor, which reads a
    // bare YYYY-MM-DD as UTC midnight and flips Upcoming/Behind Schedule a day early west of UTC.
    // Check if upcoming (start date is in the future)
    if (task.startTime) {
      const startDate = parseDate(task.startTime);
      if (startDate && startDate > now) {
        return {
          displayValue: TaskDisplayValues.UPCOMING,
          color: COLORS.upcoming,
          label: TaskDisplayValues.UPCOMING,
        };
      }
    }

    // Check if behind schedule (past end date)
    const endDate = parseDate(task.endTime);
    if (endDate && endDate < now) {
      return {
        displayValue: TaskDisplayValues.BEHIND_SCHEDULE,
        color: COLORS.error,
        label: TaskDisplayValues.BEHIND_SCHEDULE,
      };
    }

    // Check assignment type - only if there are no budget requests at all
    // This prevents showing "Assigned to Subcontractor" when budget is in progress
    const hasAnyBudgetRequests = safeBudgetRequests.length > 0;
    
    if (!hasAnyBudgetRequests && (task.delegateOrganizationId || task.delegateTaskId)) {
      return {
        displayValue: TaskDisplayValues.ASSIGNED_TO_SUBCONTRACTOR,
        color: COLORS.darkBlue,
        label: TaskDisplayValues.ASSIGNED_TO_SUBCONTRACTOR,
      };
    }

    if (task.crews && task.crews.length > 0) {
      return {
        displayValue: TaskDisplayValues.ASSIGNED_TO_CREW,
        color: COLORS.green,
        label: TaskDisplayValues.ASSIGNED_TO_CREW,
      };
    }

    // Default for ACTIVE tasks with no assignment (treat as unassigned)
    return {
      displayValue: TaskDisplayValues.UNASSIGNED,
      color: COLORS.orange,
      label: TaskDisplayValues.UNASSIGNED,
    };
  }

  // Fallback for any unhandled cases (non-ACTIVE statuses without specific handling)
  return {
    displayValue: TaskDisplayValues.UNASSIGNED,
    color: COLORS.darkUltraGray,
    label: 'UNKNOWN',
  };
}

/**
 * Gets just the color for a task status (for backward compatibility)
 */
export function getTaskStatusColor(
  task: any,
  budgetRequests: any[] = [],
): string {
  return getTaskStatusDisplay(task, budgetRequests).color;
}

/**
 * Gets just the display label for a task status
 *
 * Sprint 28 change request (global sweep, defect fix): returns the raw `displayValue`/`'UNKNOWN'`
 * now, not English text — see `TaskDisplayInfo.label`'s comment above. Callers translate through
 * `translateTaskDisplayValue`.
 */
export function getTaskStatusLabel(
  task: any,
  budgetRequests: any[] = [],
): string {
  return getTaskStatusDisplay(task, budgetRequests).label;
}

/**
 * Sprint 28 change request (global sweep, defect fix). Translates a `getTaskStatusDisplay`
 * result into display text. Reuses the already-shipped `taskDetail.status*` keys (house style
 * §5) for the seven values that overlap plain `TaskStatus` — their English text is
 * byte-identical to this file's former hardcoded labels ('Complete', 'Pending Client Approval',
 * etc.), so no new keys were needed for those. Only the derived-only values (UPCOMING,
 * BEHIND_SCHEDULE, ASSIGNED_TO_CREW, ASSIGNED_TO_SUBCONTRACTOR, IN_NEGOTIATIONS, UNASSIGNED) get
 * new `taskDetail.display*` keys. Mirrors mobile's `translateTaskDisplayValue` in
 * `redesign/src/utils/taskDisplay.utils.ts` — keep the two in sync (Decision 1's shared-name
 * rule).
 */
export function translateTaskDisplayValue(
  displayValue: TaskDisplayValues,
  // Sprint 29 (Addendum 10/10a typed-hook port). Widened from `Record<string, string>` to the
  // real `taskDetail` section type — not `Record<string, any>`, which would erase compile-time
  // checking on the 13 keys this function reads (`statusComplete`, `displayUpcoming`, ...,
  // `statusUnknown`) and reopen exactly the hole the typed-hook port exists to close, at the
  // function that feeds the task status badge across the task detail header, crews assigned-tasks
  // view, schedule row, and create/edit task modal. Real callers pass the whole `taskDetail`
  // section, which (unlike mobile's flat equivalent) has nested sub-objects (`activityTab`,
  // `budget`, `equipment`, ...) — this function only ever reads flat top-level string keys off it,
  // so the old `Record<string, string>` was inaccurate about the nesting without needing to also
  // give up key-level checking.
  tTaskDetail: (typeof en)['taskDetail'],
): string {
  switch (displayValue) {
    case TaskDisplayValues.COMPLETE:
      return tTaskDetail.statusComplete;
    case TaskDisplayValues.CLIENT_APPROVAL:
      return tTaskDetail.statusClientApproval;
    case TaskDisplayValues.INTERNAL_APPROVAL:
      return tTaskDetail.statusInternalApproval;
    case TaskDisplayValues.INACTIVE:
      return tTaskDetail.statusInactive;
    case TaskDisplayValues.REJECTED:
      return tTaskDetail.statusRejected;
    case TaskDisplayValues.CANCELED:
      return tTaskDetail.statusCanceled;
    case TaskDisplayValues.DELEGATION_CANCELED:
      return tTaskDetail.statusDelegationCanceled;
    case TaskDisplayValues.UPCOMING:
      return tTaskDetail.displayUpcoming;
    case TaskDisplayValues.BEHIND_SCHEDULE:
      return tTaskDetail.displayBehindSchedule;
    case TaskDisplayValues.ASSIGNED_TO_SUBCONTRACTOR:
      return tTaskDetail.displayAssignedToSubcontractor;
    case TaskDisplayValues.ASSIGNED_TO_CREW:
      return tTaskDetail.displayAssignedToCrew;
    case TaskDisplayValues.IN_NEGOTIATIONS:
      return tTaskDetail.displayInNegotiations;
    case TaskDisplayValues.UNASSIGNED:
      return tTaskDetail.displayUnassigned;
    default:
      return tTaskDetail.statusUnknown;
  }
}

/**
 * Gets color for a status string (for legacy activity components)
 * Maps old string status values to appropriate colors
 */
export function getTaskStatusColorFromString(statusString: string): string {
  const normalizedStatus = statusString?.toLowerCase();

  switch (normalizedStatus) {
    case 'on track':
    case 'assigned to crew':
      return COLORS.green;
    case 'for approval':
    case 'pending client approval':
    case 'client approval':
      return COLORS.darkGreen;
    case 'internal approval':
    case 'pending internal approval':
      return COLORS.darkerGreen;
    case 'delayed':
    case 'behind schedule':
      return COLORS.error;
    case 'completed':
    case 'complete':
      return COLORS.lightBlue;
    case 'rejected':
      return COLORS.error;
    case 'canceled':
      return COLORS.cancel;
    case 'delegation canceled':
      return COLORS.violet;
    case 'inactive':
      return COLORS.orange;
    case 'upcoming':
      return COLORS.upcoming;
    case 'unassigned':
      return COLORS.orange;
    case 'assigned to subcontractor':
      return COLORS.darkBlue;
    case 'in negotiations':
      return COLORS.negociations;
    default:
      return COLORS.orange; // fallback color
  }
}
